import { ReactNode } from 'react'

import { ITableColumn } from 'partner360/components/financialTable'
import { FinancialTable } from 'partner360/components/financialTable'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'

import { IChartSettings } from '../../../QBRBarChart/models'
import { QBRBarChart } from '../../../QBRBarChart/QBRBarChart'
import { TABLE_HIERARCHY_FIELD, TABLE_KEY_FIELD } from '../../configs'
import { TItemWithChildren } from '../../models'

import { QbrSubCharts } from './components/QbrSubCharts'

import styles from './QbrChartsGroup.module.scss'

interface IQbrChartsGroup<T> {
  item: TItemWithChildren<T>
  selectedPeriodFYear: number
  selectedPeriodFQuarters: number[]
  chartLabelNode?: ReactNode
  chartNameField: string
  getBarsSettings: (item: TItemWithChildren<T>) => IChartSettings[]
  getLinesSettings: (item: T) => IChartSettings[]
  withoutTrendLine?: boolean
  columns: ITableColumn<T>[]
  isSob?: boolean
}

export const QbrChartsGroup = <T extends { ParentId: number | null; EntryId: number }>({
  item,
  selectedPeriodFYear,
  selectedPeriodFQuarters,
  chartLabelNode,
  chartNameField,
  getBarsSettings,
  withoutTrendLine,
  columns,
  getLinesSettings,
  isSob,
}: IQbrChartsGroup<T>) => {
  if (item.ParentId === null) {
    return (
      <div>
        <div className={styles.rootChartWrapper}>
          <div className={styles.rootChart}>
            <div>
              <QBRBarChart
                key={item[TABLE_KEY_FIELD]}
                selectedPeriodFYear={selectedPeriodFYear}
                selectedPeriodFQuarters={selectedPeriodFQuarters}
                chartLabelNode={chartLabelNode}
                barsSettings={getBarsSettings(item)}
                linesSettings={getLinesSettings(item)}
                height={400}
                isSob={isSob}
              />
            </div>
            <div className={styles.tableWrapper}>
              <FinancialTable<T>
                hierarchyField={TABLE_HIERARCHY_FIELD}
                keyField={TABLE_KEY_FIELD}
                columns={columns}
                data={[item]}
                className={styles.tableStyle}
              />
            </div>
          </div>
        </div>
        {item?.children?.map((child) => (
          <QbrChartsGroup
            key={child.EntryId}
            chartLabelNode={chartLabelNode}
            item={child}
            selectedPeriodFYear={selectedPeriodFYear}
            selectedPeriodFQuarters={selectedPeriodFQuarters}
            chartNameField={chartNameField}
            getBarsSettings={getBarsSettings}
            getLinesSettings={getLinesSettings}
            withoutTrendLine={withoutTrendLine}
            columns={columns}
            isSob={isSob}
          />
        ))}
      </div>
    )
  }

  return (
    <div className={styles.chartsWrapper}>
      <div className={styles.chartsWrapper__main_item}>
        <QBRBarChart
          key={item[TABLE_KEY_FIELD]}
          selectedPeriodFYear={selectedPeriodFYear}
          selectedPeriodFQuarters={selectedPeriodFQuarters}
          chartLabelNode={chartLabelNode}
          chartName={item[chartNameField]}
          barsSettings={getBarsSettings(item)}
          linesSettings={getLinesSettings(item)}
          height={400}
          isSob={isSob}
        />
        <div className={styles.tableWrapper}>
          <FinancialTable<T>
            hierarchyField={TABLE_HIERARCHY_FIELD}
            keyField={TABLE_KEY_FIELD}
            columns={columns}
            data={[item]}
            className={styles.tableStyle}
          />
        </div>
      </div>
      <div className={styles.subCharts}>
        <QbrSubCharts
          selectedPeriodFYear={selectedPeriodFYear}
          selectedPeriodFQuarters={selectedPeriodFQuarters}
          getLinesSettings={getLinesSettings}
          currencySettings={chartLabelNode}
          getBarsSettings={getBarsSettings}
          chartNameField={chartNameField}
          columns={columns}
          item={item}
          title={TRANSLATION_KEYS.charts.firstChildrenLevel}
          translationReplaceParams={[{ key: '{rootName}', value: item[chartNameField] }]}
          isSob={isSob}
        />
      </div>
    </div>
  )
}
